import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import Counter from './Counter';

const { FiStar, FiQuote, FiFilter, FiAward, FiDownload, FiExternalLink } = FiIcons;

const Testimonials = () => {
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [selectedTestimonial, setSelectedTestimonial] = useState(null);

  const testimonials = [
    {
      name: 'Jean-Pierre Nkomo',
      company: 'Industrie Manufacturière',
      role: 'Directeur Général',
      image: 'https://images.unsplash.com/photo-1572561300743-2dd367ed0c9a?auto=format&fit=crop&q=80&w=400',
      text: 'M.A.M Consulting a transformé notre approche de la qualité. En 6 mois, nous avons obtenu notre certification ISO 9001. L\'équipe est professionnelle et à l\'écoute.',
      rating: 5,
      certification: 'ISO 9001',
      results: 'Certification obtenue en 6 mois'
    },
    {
      name: 'Marie Assam',
      company: 'Secteur Minier',
      role: 'Responsable HSE',
      image: 'https://images.unsplash.com/photo-1589156280159-27698a70f29e?auto=format&fit=crop&q=80&w=400',
      text: 'Excellente formation ISO 45001. Les formateurs maîtrisent parfaitement leur sujet et adaptent le contenu à notre contexte. Très recommandé.',
      rating: 5,
      certification: 'ISO 45001',
      results: 'Conformité HSE complète'
    },
    {
      name: 'Alain Tchoua',
      company: 'Construction',
      role: 'Chef de Projet',
      image: 'https://images.unsplash.com/photo-1531384441138-2736e62e0919?auto=format&fit=crop&q=80&w=400',
      text: 'L\'audit réalisé par M.A.M a identifié des points critiques que nous n\'avions pas vus. Les recommandations ont augmenté notre productivité de 20%.',
      rating: 5,
      certification: 'ISO 9001',
      results: '+20% de productivité'
    },
    {
      name: 'Sophie Mba',
      company: 'Services',
      role: 'Directrice Qualité',
      image: 'https://images.unsplash.com/photo-1567532939604-b6b5b0db2604?auto=format&fit=crop&q=80&w=400',
      text: 'Partenaire de confiance depuis 2024. Leur approche méthodologique et leur bienveillance font la différence. Merci pour votre soutien.',
      rating: 5,
      certification: 'ISO 14001',
      results: 'Gestion environnementale optimisée'
    },
    {
      name: 'Paul Ekotto',
      company: 'Secteur Énergétique',
      role: 'Manager Opérationnel',
      image: 'https://images.unsplash.com/photo-1506277886164-e25aa3f4ef7f?auto=format&fit=crop&q=80&w=400',
      text: 'Formation ISO 14001 très complète. Les cas pratiques nous ont permis d\'appliquer immédiatement les concepts. Équipe engagée et disponible.',
      rating: 5,
      certification: 'ISO 14001',
      results: 'Réduction des impacts environnementaux'
    },
    {
      name: 'Claudine Ngo',
      company: 'Santé',
      role: 'Directrice Administrative',
      image: 'https://images.unsplash.com/photo-1590650153855-d9e808231d41?auto=format&fit=crop&q=80&w=400',
      text: 'Accompagnement exemplaire pour notre transition ISO 9001. M.A.M a su nous guider avec pragmatisme et professionnalisme.',
      rating: 5,
      certification: 'ISO 9001',
      results: 'Système qualité déployé'
    }
  ];

  const isoResources = [
    {
      standard: 'ISO 9001:2015',
      title: 'Systèmes de Management de la Qualité',
      description: 'Établissez un système qualité robuste pour améliorer la satisfaction client.',
      benefits: ['Amélioration continue', 'Réduction des défauts', 'Satisfaction client accrue'],
      learnMore: '/services/formation'
    },
    {
      standard: 'ISO 14001:2015',
      title: 'Management Environnemental',
      description: 'Réduisez vos impacts environnementaux et conformez-vous à la réglementation.',
      benefits: ['Conformité légale', 'Réduction des coûts', 'Image d\'entreprise renforcée'],
      learnMore: '/services/formation'
    },
    {
      standard: 'ISO 45001:2018',
      title: 'Santé et Sécurité au Travail',
      description: 'Protégez vos collaborateurs et réduisez les risques d\'accident.',
      benefits: ['Zéro accident', 'Productivité accrue', 'Engagement des équipes'],
      learnMore: '/services/formation'
    }
  ];

  const stats = [
    { value: 150, suffix: '+', label: 'Clients Satisfaits' },
    { value: 98, suffix: '%', label: 'Taux de Satisfaction' },
    { value: 45, suffix: '+', label: 'Certifications Obtenues' },
    { value: 6, suffix: '', label: 'Ans d\'Excellence' }
  ];

  // Logic de filtrage robuste
  const filteredTestimonials = selectedFilter === 'all' 
    ? testimonials 
    : testimonials.filter(t => t.certification === selectedFilter || t.certification.includes(selectedFilter));

  const certifications = ['all', 'ISO 9001', 'ISO 14001', 'ISO 45001'];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { 
      y: 0, 
      opacity: 1,
      transition: { duration: 0.3 }
    },
    exit: { 
      scale: 0.9, 
      opacity: 0,
      transition: { duration: 0.2 }
    }
  };

  return (
    <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50 dark:from-gray-900 dark:to-gray-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-800 dark:text-white mb-4">
            Témoignages de Nos Clients
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
            Découvrez comment M.A.M Consulting a aidé des organisations à atteindre l'excellence
          </p>
        </motion.div>

        {/* Filter Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="flex flex-wrap justify-center gap-3 mb-12"
        >
          {certifications.map((cert) => (
            <motion.button
              key={cert}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setSelectedFilter(cert)}
              className={`px-6 py-2 rounded-full font-medium transition-all ${
                selectedFilter === cert
                  ? 'bg-gradient-to-r from-blue-600 to-cyan-500 text-white shadow-lg'
                  : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 hover:border-blue-600 dark:hover:border-blue-400'
              }`}
            >
              <SafeIcon icon={FiFilter} className="inline mr-2 w-4 h-4" />
              {cert === 'all' ? 'Tous' : cert}
            </motion.button>
          ))}
        </motion.div>

        {/* Testimonials Grid */}
        <motion.div
          layout
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16 min-h-[400px]"
        >
          <AnimatePresence mode="popLayout">
            {filteredTestimonials.map((testimonial) => (
              <motion.div
                layout
                key={testimonial.name}
                variants={itemVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                whileHover={{ y: -5, boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' }}
                onClick={() => setSelectedTestimonial(testimonial)}
                className="bg-white dark:bg-gray-800 rounded-xl p-8 shadow-lg hover:shadow-xl transition-all cursor-pointer group h-full flex flex-col"
              >
                {/* Quote Icon */}
                <div className="mb-4 text-blue-400 dark:text-blue-300 group-hover:scale-110 transition-transform">
                  <SafeIcon icon={FiQuote} className="w-8 h-8" />
                </div>

                {/* Rating */}
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <SafeIcon
                      key={i}
                      icon={FiStar}
                      className="w-5 h-5 text-yellow-400 fill-yellow-400"
                    />
                  ))}
                </div>

                {/* Testimonial Text */}
                <p className="text-gray-600 dark:text-gray-300 mb-6 leading-relaxed line-clamp-4 flex-grow">
                  "{testimonial.text}"
                </p>

                {/* Certification Badge */}
                <div className="mb-4 inline-block">
                  <span className="inline-flex items-center px-3 py-1 bg-gradient-to-r from-blue-100 to-cyan-100 dark:from-blue-900/30 dark:to-cyan-900/30 text-blue-700 dark:text-blue-300 text-sm font-semibold rounded-full">
                    <SafeIcon icon={FiAward} className="w-4 h-4 mr-2" />
                    {testimonial.certification}
                  </span>
                </div>

                {/* Result Highlight */}
                <p className="text-sm text-green-600 dark:text-green-400 font-medium mb-6 pb-6 border-b border-gray-200 dark:border-gray-700">
                  ✓ {testimonial.results}
                </p>

                {/* Client Info */}
                <div className="flex items-center space-x-4">
                  <img
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full object-cover ring-2 ring-blue-200 dark:ring-blue-900"
                  />
                  <div>
                    <h4 className="font-bold text-gray-800 dark:text-white">
                      {testimonial.name}
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {testimonial.role}
                    </p>
                    <p className="text-xs text-blue-600 dark:text-blue-400">
                      {testimonial.company}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>

        {/* ISO Resources Section */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-20 pt-20 border-t border-gray-200 dark:border-gray-700"
        >
          <div className="text-center mb-12">
            <h3 className="text-3xl md:text-4xl font-bold text-gray-800 dark:text-white mb-4">
              Ressources ISO et Certification
            </h3>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              Accédez à nos guides complets et ressources pour vos certifications ISO
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {isoResources.map((resource, index) => (
              <motion.div
                key={index}
                initial={{ y: 50, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -8 }}
                className="bg-white dark:bg-gray-800 rounded-xl p-8 shadow-lg hover:shadow-xl transition-all border border-gray-100 dark:border-gray-700"
              >
                <div className="inline-block px-4 py-2 bg-gradient-to-r from-blue-100 to-cyan-100 dark:from-blue-900/30 dark:to-cyan-900/30 rounded-lg mb-4">
                  <span className="text-blue-700 dark:text-blue-300 font-bold text-sm">
                    {resource.standard}
                  </span>
                </div>
                <h4 className="text-xl font-bold text-gray-800 dark:text-white mb-3">
                  {resource.title}
                </h4>
                <p className="text-gray-600 dark:text-gray-300 mb-6">
                  {resource.description}
                </p>
                <div className="mb-6">
                  <p className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-3">
                    Bénéfices clés :
                  </p>
                  <ul className="space-y-2">
                    {resource.benefits.map((benefit, idx) => (
                      <li
                        key={idx}
                        className="flex items-center text-sm text-gray-600 dark:text-gray-400"
                      >
                        <span className="w-2 h-2 bg-blue-600 dark:bg-blue-400 rounded-full mr-3"></span>
                        {benefit}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="flex gap-3">
                  <Link to={resource.learnMore} className="flex-1">
                    <motion.div
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="flex items-center justify-center px-4 py-2 border-2 border-blue-600 dark:border-blue-400 text-blue-600 dark:text-blue-400 font-semibold rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all w-full"
                    >
                      <SafeIcon icon={FiExternalLink} className="w-4 h-4 mr-2" />
                      Détails
                    </motion.div>
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Stats Section with Animated Counters */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: false }}
          className="mt-20 pt-20 border-t border-gray-200 dark:border-gray-700"
        >
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ scale: 0.8, opacity: 0 }}
                whileInView={{ scale: 1, opacity: 1 }}
                viewport={{ once: false }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="text-3xl md:text-4xl font-bold text-blue-600 dark:text-blue-400 mb-2">
                  <Counter to={stat.value} suffix={stat.suffix} duration={2.5} />
                </div>
                <p className="text-gray-600 dark:text-gray-400 font-medium">
                  {stat.label}
                </p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Modal for Full Testimonial */}
      <AnimatePresence>
        {selectedTestimonial && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedTestimonial(null)}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white dark:bg-gray-800 rounded-2xl p-8 max-w-2xl w-full"
            >
              <div className="flex items-center space-x-4 mb-6">
                <img
                  src={selectedTestimonial.image}
                  alt={selectedTestimonial.name}
                  className="w-16 h-16 rounded-full object-cover ring-2 ring-blue-200 dark:ring-blue-900"
                />
                <div>
                  <h4 className="text-xl font-bold text-gray-800 dark:text-white">
                    {selectedTestimonial.name}
                  </h4>
                  <p className="text-gray-600 dark:text-gray-400">
                    {selectedTestimonial.role} - {selectedTestimonial.company}
                  </p>
                </div>
              </div>
              <div className="flex gap-1 mb-4">
                {[...Array(selectedTestimonial.rating)].map((_, i) => (
                  <SafeIcon
                    key={i}
                    icon={FiStar}
                    className="w-5 h-5 text-yellow-400 fill-yellow-400"
                  />
                ))}
              </div>
              <p className="text-gray-700 dark:text-gray-300 text-lg leading-relaxed mb-6">
                "{selectedTestimonial.text}"
              </p>
              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 rounded-lg p-4 mb-6">
                <p className="text-green-700 dark:text-green-400 font-semibold">
                  ✓ Résultat: {selectedTestimonial.results}
                </p>
                <p className="text-blue-700 dark:text-blue-300 font-semibold mt-2">
                  Certification: {selectedTestimonial.certification}
                </p>
              </div>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setSelectedTestimonial(null)}
                className="w-full px-6 py-3 bg-gradient-to-r from-blue-600 to-cyan-500 text-white font-semibold rounded-lg hover:from-blue-700 hover:to-cyan-600 transition-all"
              >
                Fermer
              </motion.button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Testimonials;